import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/textarea';
import { DatePicker } from '../ui/DatePicker';
import { Spinner } from '../ui/Spinner';
import { EventTypeSelect } from './EventTypeSelect';
import { DossierPickStep } from '../quickCreate/DossierPickStep';
import { useAuthStore } from '../../store/useAuthStore';
import { useCollaborators } from '../../hooks/queries/useCollaborators';
import {
  useAppointment,
  useCreateAppointment,
  useDeleteAppointment,
  useUpdateAppointment,
} from '../../hooks/queries/useAppointments';
import { DEFAULT_FICHE_DUE_TIME } from '../../lib/tasks/taskDueTime';
import {
  appointmentRangeError,
  canSubmitAppointment,
  ficheToInstant,
  instantToFiche,
  shiftQuarterHour,
} from '../../lib/appointments/appointmentDraft';

const CABINET_ROLES = new Set(['admin', 'lawyer', 'secretary']);

export function AppointmentDialog({
  open,
  appointmentId,
  contextDossierId,
  onClose,
}: {
  open: boolean;
  appointmentId: string | null;
  contextDossierId: string | null;
  onClose: () => void;
}) {
  const meId = useAuthStore((s) => s.collaborator?.id);
  const { data: collaborators } = useCollaborators();
  const existing = useAppointment(open ? appointmentId : null);
  const create = useCreateAppointment();
  const update = useUpdateAppointment();
  const remove = useDeleteAppointment();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startDay, setStartDay] = useState('');
  const [startTime, setStartTime] = useState(DEFAULT_FICHE_DUE_TIME);
  const [endDay, setEndDay] = useState('');
  const [endTime, setEndTime] = useState(DEFAULT_FICHE_DUE_TIME);
  const [dossierId, setDossierId] = useState<string | null>(contextDossierId);
  const [eventTypeId, setEventTypeId] = useState<string | null>(null);
  const [participantIds, setParticipantIds] = useState<string[]>([]);
  const seeded = useRef(false);
  const loaded = useRef<string | null>(null);
  // DatePicker commits the day and the time in the same turn. A ref keeps
  // the half that was just set, because React state is still the previous one.
  const startDayRef = useRef(startDay);
  const startTimeRef = useRef(startTime);
  startDayRef.current = startDay;
  startTimeRef.current = startTime;

  useEffect(() => {
    if (!open) {
      seeded.current = false;
      loaded.current = null;
      return;
    }
    if (appointmentId) return;
    setTitle('');
    setDescription('');
    setStartDay('');
    setStartTime(DEFAULT_FICHE_DUE_TIME);
    setEndDay('');
    setEndTime(DEFAULT_FICHE_DUE_TIME);
    setDossierId(contextDossierId);
    setEventTypeId(null);
    setParticipantIds(meId ? [meId] : []);
    seeded.current = true;
  }, [open, appointmentId, contextDossierId, meId]);

  useEffect(() => {
    const row = existing.data;
    if (!open || !row || loaded.current === row.id) return;
    loaded.current = row.id;
    const start = instantToFiche(row.starts_at);
    const end = instantToFiche(row.ends_at);
    setTitle(row.title);
    setDescription(row.description ?? '');
    setStartDay(start.day);
    setStartTime(start.time);
    setEndDay(end.day);
    setEndTime(end.time);
    setDossierId(row.dossier_id);
    setEventTypeId(row.event_type_id);
    setParticipantIds(row.participantIds);
  }, [open, existing.data]);

  const staff = useMemo(() => {
    const rows = (collaborators ?? []).filter(
      (c) => c.is_active && CABINET_ROLES.has(c.role),
    );
    return rows.sort((a, b) => {
      if (a.id === meId) return -1;
      if (b.id === meId) return 1;
      return a.full_name.localeCompare(b.full_name, 'fr');
    });
  }, [collaborators, meId]);

  const draft = {
    title,
    startDay,
    startTime,
    endDay,
    endTime,
    participantIds,
    dossierId,
  };
  const rangeError = appointmentRangeError(draft);
  const pending = create.isPending || update.isPending || remove.isPending;

  function commitStart(day: string, time: string) {
    startDayRef.current = day;
    startTimeRef.current = time;
    setStartDay(day);
    setStartTime(time);
    const startIso = ficheToInstant(day, time);
    if (!startIso) return;
    const endIso = ficheToInstant(endDay, endTime);
    if (!endIso || new Date(endIso).getTime() <= new Date(startIso).getTime()) {
      const next = shiftQuarterHour(day, time, 60);
      setEndDay(next.day);
      setEndTime(next.time);
    }
  }

  function toggleParticipant(id: string) {
    setParticipantIds((cur) => {
      if (cur.includes(id)) {
        if (cur.length === 1) return cur;
        return cur.filter((x) => x !== id);
      }
      return [...cur, id];
    });
  }

  function save() {
    if (!canSubmitAppointment(draft)) return;
    const starts_at = ficheToInstant(startDay, startTime);
    const ends_at = ficheToInstant(endDay, endTime);
    if (!starts_at || !ends_at) return;
    const body = {
      title: title.trim(),
      description: description.trim() || null,
      starts_at,
      ends_at,
      dossier_id: dossierId,
      event_type_id: eventTypeId,
      participantIds,
    };
    const done = {
      onSuccess: () => {
        toast.success(appointmentId ? 'Rendez-vous enregistré' : 'Rendez-vous créé');
        onClose();
      },
      onError: (err: unknown) => {
        toast.error(err instanceof Error ? err.message : 'Impossible d’enregistrer');
      },
    };
    if (appointmentId) {
      update.mutate({ id: appointmentId, patch: body }, done);
      return;
    }
    create.mutate(body, done);
  }

  function destroy() {
    if (!appointmentId) return;
    remove.mutate(appointmentId, {
      onSuccess: () => {
        toast.success('Rendez-vous supprimé');
        onClose();
      },
      onError: (err) => {
        toast.error(err instanceof Error ? err.message : 'Impossible de supprimer');
      },
    });
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-cime shadow-xl w-full max-w-xl overflow-hidden flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="appointment-dialog-title"
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
          <h2 id="appointment-dialog-title" className="font-heading text-lg font-semibold text-cime-text-primary">
            {appointmentId ? 'Rendez-vous' : 'Nouveau rendez-vous'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-700"
            aria-label="Fermer"
          >
            <X className="w-4 h-4" />
          </button>
        </header>
        {appointmentId && existing.isLoading ? (
          <div className="flex justify-center py-10"><Spinner /></div>
        ) : appointmentId && existing.isError ? (
          <p className="px-5 py-8 text-sm text-neutral-400 text-center">Rendez-vous introuvable</p>
        ) : (
          <div className="overflow-y-auto">
            <DossierPickStep
              selectedId={dossierId}
              onSelect={setDossierId}
              required={false}
              onClear={() => setDossierId(null)}
            />
            <div className="p-5 space-y-4">
              <label className="block space-y-1.5">
                <span className="text-xs font-semibold uppercase tracking-wide text-cime-text-muted">
                  Titre
                </span>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Point dossier" />
              </label>
              <div className="space-y-1.5">
                <span className="text-xs font-semibold uppercase tracking-wide text-cime-text-muted">
                  Type
                </span>
                <EventTypeSelect family="appointment" value={eventTypeId} onChange={setEventTypeId} />
              </div>
              <label className="block space-y-1.5">
                <span className="text-xs font-semibold uppercase tracking-wide text-cime-text-muted">
                  Description
                </span>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Objet du rendez-vous, points à aborder"
                  rows={3}
                />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block space-y-1.5 min-w-0">
                  <span className="text-xs font-semibold uppercase tracking-wide text-cime-text-muted">
                    Début
                  </span>
                  <DatePicker
                    value={startDay}
                    onChange={(day) => commitStart(day, startTimeRef.current)}
                    showTime
                    time={startTime}
                    onTimeChange={(time) => commitStart(startDayRef.current, time)}
                    aria-label="Début du rendez-vous"
                  />
                </label>
                <label className="block space-y-1.5 min-w-0">
                  <span className="text-xs font-semibold uppercase tracking-wide text-cime-text-muted">
                    Fin
                  </span>
                  <DatePicker
                    value={endDay}
                    onChange={setEndDay}
                    showTime
                    time={endTime}
                    onTimeChange={setEndTime}
                    aria-label="Fin du rendez-vous"
                  />
                </label>
              </div>
              {rangeError ? (
                <p className="text-xs text-cime-urgent">{rangeError}</p>
              ) : null}
              <fieldset className="space-y-2">
                <legend className="text-xs font-semibold uppercase tracking-wide text-cime-text-muted">
                  Intervenants
                </legend>
                <ul className="max-h-40 overflow-y-auto rounded-cime border border-neutral-200 divide-y divide-neutral-100">
                  {staff.map((person) => {
                    const checked = participantIds.includes(person.id);
                    return (
                      <li key={person.id}>
                        <label className="flex items-center gap-2 px-3 py-2 text-sm text-cime-text-primary">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleParticipant(person.id)}
                          />
                          <span>
                            {person.full_name}
                            {person.id === meId ? ' (Vous)' : ''}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
                <p className="text-xs text-cime-text-muted">
                  Au moins un intervenant. Vous pouvez vous retirer si quelqu’un d’autre reste coché.
                </p>
              </fieldset>
              <div className="flex items-center justify-between gap-2 pt-1">
                {appointmentId ? (
                  <Button type="button" variant="danger" onClick={destroy} disabled={pending}>
                    Supprimer
                  </Button>
                ) : (
                  <span />
                )}
                <div className="flex items-center gap-2">
                  <Button type="button" variant="secondary" onClick={onClose} disabled={pending}>
                    Annuler
                  </Button>
                  <Button
                    type="button"
                    onClick={save}
                    disabled={!canSubmitAppointment(draft) || pending}
                    isLoading={create.isPending || update.isPending}
                  >
                    {appointmentId ? 'Enregistrer' : 'Créer le rendez-vous'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
